import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles } from '@/mocks/data';

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || categories[0].slug;
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 6;

  const activeCategory = categories.find((c) => c.slug === activeTab) || categories[0];
  const filteredArticles = articles.filter((a) => a.category === activeTab);
  const totalPages = Math.ceil(filteredArticles.length / perPage);
  const displayedArticles = filteredArticles.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage,
  );

  useEffect(() => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab]);

  const handleTabChange = (slug: string) => {
    setSearchParams({ tab: slug });
  };

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategory.name} - 糖心视频`,
    description: activeCategory.description,
    url: `https://www.tangxinshipin.com/category?tab=${activeTab}`,
  };

  return (
    <>
      <SEO
        title={`${activeCategory.name} - 糖心视频 | 超清画质极速播放`}
        description={`${activeCategory.description}糖心视频${activeCategory.name}提供超清画质、极速播放的在线观影体验。`}
        keywords={`${activeCategory.name}, 糖心视频, 高清在线, 超清画质, 极速播放`}
        canonicalUrl={`https://www.tangxinshipin.com/category?tab=${activeTab}`}
        schemaData={categorySchema}
      />

      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-6 py-4">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览', href: '/category' },
              { label: activeCategory.name },
            ]}
          />
        </div>

        <section className="py-4 md:py-6 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-2">
                {activeCategory.name}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base mb-8">
                {activeCategory.description}
              </p>

              <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 flex-nowrap">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleTabChange(cat.slug)}
                    className={`whitespace-nowrap cursor-pointer px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      activeTab === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                    <span className="ml-1.5 text-xs opacity-70">
                      ({cat.count})
                    </span>
                  </button>
                ))}
              </div>

              {displayedArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {displayedArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group cursor-pointer bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
                    >
                      <div className="relative h-44 md:h-48 overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="inline-block px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                            {article.categoryName}
                          </span>
                        </div>
                      </div>
                      <div className="p-4 md:p-5">
                        <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-foreground-500 text-xs md:text-sm leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-foreground-400">
                          <span>{article.publishDate}</span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {article.views.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-film-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">
                    该分类暂无内容，请浏览其他分类
                  </p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="whitespace-nowrap cursor-pointer w-9 h-9 rounded-lg flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`whitespace-nowrap cursor-pointer w-9 h-9 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="whitespace-nowrap cursor-pointer w-9 h-9 rounded-lg flex items-center justify-center bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}